/**
 * Coach-onboarding-funnel intent consumption + fork answers (U5/U6 of the
 * onboarding-funnel plan). `consumePostAuthIntentFn`/`recordIntentAnswerFn`
 * are thin glue (auth + cookie read + delegation); the actual write/backfill
 * logic lives in `~/lib/server/user-intent` so it's directly testable and —
 * critically — never statically reachable from the client bundle (this file
 * is imported by `_user.tsx` and other client routes).
 *
 * Aliases: post-auth intent server fn, consumePostAuthIntentFn,
 * recordIntentAnswerFn, fork answer server fn
 * @module user-intent
 */
import { createServerFn } from "@tanstack/react-start";
import { deleteCookie, getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  POST_AUTH_COOKIE_NAMES,
  forkAnswerSchema,
} from "~/lib/shared/post-auth-routing";

// Client-safe type re-exports so existing importers keep working; the value
// implementations live server-side only.
export type {
  ConsumePostAuthIntentResult,
  RecordIntentAnswerParams,
} from "~/lib/server/user-intent";
export type { ForkAnswer } from "~/lib/shared/post-auth-routing";

/**
 * Server fn wrapping `consumePostAuthIntent`: auth + cookie header read, then
 * clears the post-auth cookies server-side on the same response (h3's
 * `deleteCookie` attaches a real `Set-Cookie` header to this RPC call's HTTP
 * response, which the browser applies same as any other same-origin fetch —
 * see `clearRefCodeCookie`'s identical pattern in `saveUserSignup`). This is
 * the only consumption point for a Clerk OAuth session that lands directly on
 * a bare home surface (`_user.tsx`'s fork gate) without ever going through
 * login's `handleSuccess`, so relying solely on the client-side clear there
 * would leave the cookies live — readable by a different account signing in
 * on the same browser within the TTL — until this path also clears them.
 * `clearPostAuthCookiesClientSide` remains a client-side belt for the
 * `handleSuccess` path (which already has the cookies in hand from its own
 * call to this function) rather than the sole mechanism.
 */
export const consumePostAuthIntentFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { consumePostAuthIntent } = await import("~/lib/server/user-intent");
    const db = await getDBWithInit();
    const cookieHeader = getRequestHeader("cookie") ?? "";

    const result = await consumePostAuthIntent(
      db,
      context.user.id,
      cookieHeader,
    );

    for (const name of Object.values(POST_AUTH_COOKIE_NAMES)) {
      deleteCookie(name, { path: "/" });
    }

    return result;
  });

/**
 * Server fn wrapping `recordIntentAnswer`: auth + input parsing only.
 */
export const recordIntentAnswerFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      answer: forkAnswerSchema,
      allowOverwrite: z.boolean().optional(),
    }),
  )
  .handler(async ({ context, data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { recordIntentAnswer } = await import("~/lib/server/user-intent");
    const db = await getDBWithInit();

    return recordIntentAnswer(db, {
      userId: context.user.id,
      answer: data.answer,
      allowOverwrite: data.allowOverwrite,
    });
  });

/**
 * Clear the post-auth cookies client-side. `consumePostAuthIntentFn` already
 * clears them server-side (via `deleteCookie` on its own response) on every
 * call, so this is a redundant belt for the `handleSuccess` caller rather
 * than the only clearing mechanism — kept so a client that inspects
 * `document.cookie` immediately after the RPC resolves (before the
 * `Set-Cookie` from that response has necessarily been reflected back into
 * `document.cookie` by the browser) never sees a stale value.
 */
export const clearPostAuthCookiesClientSide = (): void => {
  if (typeof document === "undefined") return;

  for (const name of Object.values(POST_AUTH_COOKIE_NAMES)) {
    document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
  }
};
